import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vcollectChoiceListsApi, VCollectFormQuestion } from '../../api/vcollect';

const RELEVANCE_OPERATORS: { value: string; label: string; xform: string }[] = [
  { value: 'eq', label: 'equals', xform: '=' },
  { value: 'neq', label: 'does not equal', xform: '!=' },
  { value: 'gt', label: 'is greater than', xform: '>' },
  { value: 'lt', label: 'is less than', xform: '<' },
  { value: 'gte', label: 'is at least', xform: '>=' },
  { value: 'lte', label: 'is at most', xform: '<=' },
];
const NUMERIC_TYPES = ['integer', 'decimal'];
const CHOICE_TYPES = ['select_one', 'select_multiple'];

/**
 * Compiles a visual condition into a real ODK/XForms relevance
 * expression (spec section 8) - never a separate VCollect expression
 * language. A choice-based reference question produces a quoted
 * string comparison ('yes'); a numeric one produces a bare number.
 */
function compileRelevance(refQuestion: VCollectFormQuestion, operatorXform: string, value: string): string {
  const isNumeric = NUMERIC_TYPES.includes(refQuestion.question_type);
  const rhs = isNumeric ? value : `'${value}'`;
  return `\${${refQuestion.name}} ${operatorXform} ${rhs}`;
}

interface LogicEditorProps {
  question: VCollectFormQuestion;
  otherQuestions: VCollectFormQuestion[];
  onSaveRelevance: (expression: string) => void;
  onClearRelevance: () => void;
  onSaveConstraint: (expression: string, message: string) => void;
  onClearConstraint: () => void;
  isSaving: boolean;
}

export function LogicEditor({ question, otherQuestions, onSaveRelevance, onClearRelevance, onSaveConstraint, onClearConstraint, isSaving }: LogicEditorProps) {
  const [refQuestionId, setRefQuestionId] = useState('');
  const [operator, setOperator] = useState('eq');
  const [value, setValue] = useState('');
  const refQuestion = otherQuestions.find((q) => q.id === Number(refQuestionId));
  const { data: choicesData } = useQuery({
    queryKey: ['vcollect-choices', refQuestion?.choice_list_id],
    queryFn: () => vcollectChoiceListsApi.listChoices(refQuestion!.choice_list_id!),
    enabled: !!refQuestion?.choice_list_id,
  });
  const choices = choicesData?.data ?? [];

  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const isNumericQuestion = NUMERIC_TYPES.includes(question.question_type);

  useEffect(() => { setValue(''); }, [refQuestionId]);

  const operatorXform = RELEVANCE_OPERATORS.find((o) => o.value === operator)?.xform ?? '=';

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 6 }}>Skip Logic (Relevance)</div>
      {question.relevance_expression ? (
        <div>
          <code style={{ fontSize: 12 }}>{question.relevance_expression}</code>
          <button className="btn-secondary" style={{ display: 'block', marginTop: 6 }} onClick={onClearRelevance} disabled={isSaving}>Remove Condition</button>
        </div>
      ) : (
        <div>
          <p className="muted" style={{ fontSize: 12 }}>Show this question only when:</p>
          <select value={refQuestionId} onChange={(e) => setRefQuestionId(e.target.value)} style={{ width: '100%', marginBottom: 6 }}>
            <option value="">Select a question…</option>
            {otherQuestions.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
          </select>
          {refQuestion && (
            <>
              <select value={operator} onChange={(e) => setOperator(e.target.value)} style={{ width: '100%', marginBottom: 6 }}>
                {RELEVANCE_OPERATORS.filter((o) => CHOICE_TYPES.includes(refQuestion.question_type) ? ['eq', 'neq'].includes(o.value) : true).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {refQuestion.choice_list_id ? (
                <select value={value} onChange={(e) => setValue(e.target.value)} style={{ width: '100%', marginBottom: 6 }}>
                  <option value="">Select value…</option>
                  {choices.map((c) => <option key={c.id} value={c.value}>{c.label}</option>)}
                </select>
              ) : (
                <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value" type={NUMERIC_TYPES.includes(refQuestion.question_type) ? 'number' : 'text'} style={{ width: '100%', marginBottom: 6 }} />
              )}
              <button onClick={() => onSaveRelevance(compileRelevance(refQuestion, operatorXform, value))} disabled={!value || isSaving}>Apply Condition</button>
            </>
          )}
          {otherQuestions.length === 0 && <p className="muted" style={{ fontSize: 12 }}>No other questions exist yet to reference.</p>}
        </div>
      )}

      {isNumericQuestion && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 6 }}>Validation (Min/Max)</div>
          {question.constraint_expression ? (
            <div>
              <code style={{ fontSize: 12 }}>{question.constraint_expression}</code>
              {question.constraint_message && <p style={{ fontSize: 12 }} className="muted">Error message: {question.constraint_message}</p>}
              <button className="btn-secondary" style={{ display: 'block', marginTop: 6 }} onClick={onClearConstraint} disabled={isSaving}>Remove Validation</button>
            </div>
          ) : (
            <div>
              <input placeholder="Minimum" type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
              <input placeholder="Maximum" type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
              <button
                onClick={() => {
                  const parts: string[] = [];
                  if (minValue) parts.push(`. >= ${minValue}`);
                  if (maxValue) parts.push(`. <= ${maxValue}`);
                  onSaveConstraint(parts.join(' and '), `Value must be${minValue ? ` at least ${minValue}` : ''}${minValue && maxValue ? ' and' : ''}${maxValue ? ` at most ${maxValue}` : ''}.`);
                }}
                disabled={(!minValue && !maxValue) || isSaving}
              >
                Apply Validation
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
